/**
 * "Type anywhere to focus the composer": decide whether a stray keydown on the
 * page should be redirected into the chat composer.
 *
 * Extracted from ChatView so the rule is unit-testable on its own (ChatView
 * itself is a multi-thousand-line component). Reads the live document for the
 * floating-layer / surface-launcher checks, exactly as it did inline.
 */

const TYPE_TO_FOCUS_EDITABLE_SELECTOR = [
  "input",
  "textarea",
  "select",
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  '[role="textbox"]',
].join(",");
const TYPE_TO_FOCUS_INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "summary",
  '[role="button"]',
  '[role="checkbox"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
].join(",");
const TYPE_TO_FOCUS_FLOATING_LAYER_SELECTOR = [
  '[data-slot="dialog"]',
  '[data-slot="menu-popup"]',
  '[data-slot="select-popup"]',
  '[data-slot="popover-popup"]',
  '[data-slot="combobox-popup"]',
  '[data-slot="autocomplete-popup"]',
].join(",");

function eventPathContainsSelector(event: Event, selector: string): boolean {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  if (path.length === 0 && event.target) {
    path.push(event.target);
  }
  return path.some((target) => target instanceof Element && target.closest(selector));
}

/**
 * True when this keystroke is a plain character typed at nothing in particular,
 * so the composer should take it.
 *
 * Exported for its unit test (the precedent is `triggerSourceOf` in
 * components/workflows/WorkflowsPage.tsx); ChatView is its only caller.
 */
export function shouldTypeToFocusComposer(event: KeyboardEvent): boolean {
  if (event.defaultPrevented || event.isComposing) return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  // `KeyboardEvent.key` is typed `string`, but the DOM lies here: real events in
  // production arrive with it ABSENT (Sentry JAVASCRIPT-3 was `event.key is
  // undefined` thrown by the `.length` below). Synthetic events dispatched by
  // extensions, password managers and autofill, plus some IME composition
  // events, carry no `key` at all. The runtime check is deliberately `typeof`
  // rather than `?.` so that lie stays visible to the next reader.
  if (typeof event.key !== "string") return false;
  if (event.key.length !== 1) return false;

  if (eventPathContainsSelector(event, TYPE_TO_FOCUS_EDITABLE_SELECTOR)) return false;
  if (eventPathContainsSelector(event, TYPE_TO_FOCUS_INTERACTIVE_SELECTOR)) return false;
  if (document.querySelector(TYPE_TO_FOCUS_FLOATING_LAYER_SELECTOR)) return false;

  // The right-panel surface launcher claims its shortcut letters while it is
  // visible (data attribute set in RightPanelTabs); those keys open surfaces
  // instead of typing into the composer.
  const launcherKeys = document
    .querySelector("[data-surface-launcher-keys]")
    ?.getAttribute("data-surface-launcher-keys");
  if (launcherKeys && launcherKeys.toLowerCase().includes(event.key.toLowerCase())) return false;

  return true;
}
